/*
 * product-variants-hv.js
 *
 * Standalone variant picker for products with more than 250 variants. Only
 * loaded by templates that opt in (product.high-variant.json). The default
 * <product-variants> element in assets/theme.js is untouched so the existing
 * default product template behaves exactly as it did on the live theme.
 *
 * The component implements Shopify's high-variant-products guidance:
 *   - reads option values from product.options_with_values rather than
 *     iterating product.variants (capped at 250)
 *   - looks up the variant for the current selection from a data-variant-json
 *     attribute embedded on each option control (the Liquid snippet
 *     product-option-value-attributes.liquid renders these)
 *   - refreshes the picker via the Section Rendering API with
 *     option_values=... so option availability + the picker's selected variant
 *     stay accurate even for variants outside the first 250
 *
 * Events dispatched (compatible with theme.js listeners):
 *   - "variant:changed" bubbling on the product form (used by product-meta,
 *     product-payment-container, product-inventory, store-pickup, etc.)
 *   - "VariantChanged" on window (used by variant-upsell, swatch button, etc.)
 */

(function () {
	if (window.customElements.get('product-variants-hv')) {
		return;
	}

	function triggerEvent(element, name, detail) {
		element.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
	}

	function safeJsonParse(value, label) {
		if (!value) {
			return null;
		}
		try {
			return JSON.parse(value);
		} catch (error) {
			console.warn('[product-variants-hv] Unable to parse ' + (label || 'JSON'), error);
			return null;
		}
	}

	var ProductVariantsHV = class extends HTMLElement {
		constructor() {
			super();
			this._lastSelectedVariant = null;
			this._onOptionChanged = this._onOptionChanged.bind(this);
			this._onMasterSelectorChanged = this._onMasterSelectorChanged.bind(this);
		}

		async connectedCallback() {
			this.formId = this.getAttribute('form-id');
			this.form = this.formId ? document.getElementById(this.formId) : null;
			this.masterSelector = this.form ? this.form.querySelector('[name="id"]') : null;
			if (!this.masterSelector) {
				console.warn('[product-variants-hv] no master selector for product ' + this.productHandle);
				return;
			}
			// The form is rendered with `<input ... disabled name="id">` so the
			// initial Liquid value doesn't ride along with the form submission
			// until JS is up; re-enable it once we're wired up.
			this.masterSelector.disabled = false;

			this.sectionId = this.getAttribute('section-id');
			this.pickerId = this.getAttribute('picker-id');
			this.productUrl = this.getAttribute('product-url') || ('/products/' + this.productHandle);

			this.addEventListener('change', this._onOptionChanged);
			this.masterSelector.addEventListener('change', this._onMasterSelectorChanged);

			this._updateDisableSelectors();

			var initial = this._getVariantFromOptions() || this._getSelectedVariantFromScript();
			if (initial) {
				this.selectVariant(initial.id, initial, { initial: true });
			}
		}

		disconnectedCallback() {
			if (this.abortController) {
				this.abortController.abort();
			}
		}

		get productHandle() {
			return this.getAttribute('handle');
		}

		get hideSoldOutVariants() {
			return this.hasAttribute('hide-sold-out-variants');
		}

		get updateUrl() {
			return this.hasAttribute('update-url');
		}

		selectVariant(id, variant, options) {
			options = options || {};
			variant = variant || this._getVariantFromOptions() || null;
			id = id || (variant && variant.id) || null;

			var lastId = this._lastSelectedVariant ? this._lastSelectedVariant.id : null;
			var currentMasterValue = this.masterSelector.value;
			var sameId = lastId === id && currentMasterValue === String(id || '');
			if (!options.force && sameId) {
				return;
			}

			this._lastSelectedVariant = variant;
			this.masterSelector.value = id || '';
			this.masterSelector.disabled = !id;

			if (!options.skipMasterChange) {
				this.masterSelector.dispatchEvent(new CustomEvent('change', {
					bubbles: true,
					detail: { variant: variant }
				}));
			}

			if (this.updateUrl && window.history && window.history.replaceState) {
				var newUrl = new URL(window.location.href);
				if (id) {
					newUrl.searchParams.set('variant', String(id));
					newUrl.searchParams.delete('option_values');
				} else {
					newUrl.searchParams.delete('variant');
					var optionValueIds = this._getSelectedOptionValueIds();
					if (optionValueIds.length) {
						newUrl.searchParams.set('option_values', optionValueIds.join(','));
					} else {
						newUrl.searchParams.delete('option_values');
					}
				}
				window.history.replaceState({ path: newUrl.toString() }, '', newUrl.toString());
			}

			this._updateDisableSelectors();

			if (this.masterSelector.form) {
				triggerEvent(this.masterSelector.form, 'variant:changed', { variant: variant });
			}

			if (!this.classList.contains('quick-buy-drawer-variants')) {
				window.dispatchEvent(new CustomEvent('VariantChanged', {
					detail: {
						available: !!(variant && variant.available),
						variant: variant
					}
				}));
			}
		}

		_onOptionChanged(event) {
			var target = event.target;
			if (!target || !target.name || target.name.indexOf('option') !== 0) {
				return;
			}
			var variant = this._getVariantFromOptions();
			this.selectVariant(variant ? variant.id : null, variant);
			this._refreshOptionValues(target);
		}

		_onMasterSelectorChanged(event) {
			var detail = event.detail || {};
			var variant = detail.variant || this._getSelectedVariantFromScript();
			var optionValues = (variant && variant.options) || [];
			optionValues.forEach((value, index) => {
				var input = this.querySelector('input[name="option' + (index + 1) + '"][value="' + CSS.escape(value) + '"], select[name="option' + (index + 1) + '"]');
				if (!input) {
					return;
				}
				var shouldFire = false;
				if (input.tagName === 'SELECT') {
					shouldFire = input.value !== value;
					input.value = value;
				} else if (input.tagName === 'INPUT') {
					shouldFire = !input.checked && input.value === value;
					input.checked = input.value === value;
				}
				if (shouldFire) {
					input.dispatchEvent(new Event('change', { bubbles: true }));
				}
			});
		}

		_getSelectedOptionControls() {
			var controls = Array.from(this.querySelectorAll('input[name^="option"]:checked, select[name^="option"]'));
			return controls.map((control) => {
				if (control.tagName === 'SELECT') {
					return control.options[control.selectedIndex];
				}
				return control;
			}).filter(Boolean);
		}

		_getSelectedOptionValues() {
			return this._getSelectedOptionControls().map((control) => control.value);
		}

		_getSelectedOptionValueIds() {
			return this._getSelectedOptionControls()
				.map((control) => control.getAttribute('data-option-value-id'))
				.filter(Boolean);
		}

		_getVariantFromOptions() {
			var selectedValues = this._getSelectedOptionValues();
			var controls = this._getSelectedOptionControls();
			for (var i = 0; i < controls.length; i++) {
				var variant = safeJsonParse(controls[i].getAttribute('data-variant-json'), 'option value variant');
				if (variant && variant.options && variant.options.every((value, index) => value === selectedValues[index])) {
					return variant;
				}
			}
			return null;
		}

		_getSelectedVariantFromScript() {
			var script = this.querySelector('[data-selected-variant]');
			if (!script || !script.textContent.trim()) {
				return null;
			}
			return safeJsonParse(script.textContent, 'selected variant');
		}

		_updateDisableSelectors() {
			var hideSoldOut = this.hideSoldOutVariants;
			this.querySelectorAll('[data-option-value-id]').forEach((control) => {
				var optionAvailable = control.getAttribute('data-option-available') !== 'false';
				var variantAvailable = control.getAttribute('data-variant-available') !== 'false';
				var wrapper = control.closest('.variant-swatch, .color-swatch, .block-swatch') || control;
				var forceHidden = wrapper.hasAttribute('data-force-hidden');

				wrapper.toggleAttribute('hidden', forceHidden || (hideSoldOut && !optionAvailable));
				wrapper.classList.toggle('is-disabled', !optionAvailable);
				wrapper.toggleAttribute('data-is-disabled', !optionAvailable);

				if (wrapper.classList.contains('variant-swatch')) {
					wrapper.classList.toggle('sold-out-swatch', !variantAvailable);
					var label = wrapper.querySelector('.variant-swatch__item');
					if (label) {
						label.classList.toggle('sold-out', !variantAvailable);
					}
				}
			});
		}

		async _refreshOptionValues(target) {
			if (!this.sectionId) {
				return;
			}
			var selectedOptionValueIds = this._getSelectedOptionValueIds();
			if (!selectedOptionValueIds.length) {
				return;
			}

			var focusedId = target ? target.id : null;
			var targetOption = target && target.tagName === 'SELECT' ? target.options[target.selectedIndex] : target;
			var productUrl = (targetOption && targetOption.getAttribute('data-product-url')) || this.productUrl;
			var url = new URL(productUrl, window.location.origin);
			url.searchParams.set('section_id', this.sectionId);
			url.searchParams.set('option_values', selectedOptionValueIds.join(','));

			if (this.abortController) {
				this.abortController.abort();
			}
			this.abortController = new AbortController();

			try {
				var response = await fetch(url.toString(), { signal: this.abortController.signal });
				if (!response.ok) {
					return;
				}
				var responseText = await response.text();
				var html = new DOMParser().parseFromString(responseText, 'text/html');

				var escapedFormId = CSS.escape(this.formId);
				var selector = this.pickerId
					? 'product-variants-hv[form-id="' + escapedFormId + '"][picker-id="' + CSS.escape(this.pickerId) + '"]'
					: 'product-variants-hv[form-id="' + escapedFormId + '"]';
				var updated = html.querySelector(selector);
				if (!updated) {
					return;
				}
				this.innerHTML = updated.innerHTML;

				// Inventory map ships with the section render; keep the
				// existing element wiring but refresh both its DOM and the
				// in-memory `inventories` lookup that listens to variant:changed.
				var inventorySelector = 'product-inventory[form-id="' + escapedFormId + '"]';
				var freshInventory = html.querySelector(inventorySelector);
				var liveInventory = document.querySelector(inventorySelector);
				if (freshInventory && liveInventory) {
					liveInventory.innerHTML = freshInventory.innerHTML;
					var inventoryScript = liveInventory.querySelector('script');
					if (inventoryScript) {
						var parsed = safeJsonParse(inventoryScript.innerHTML, 'inventory map');
						if (parsed) {
							liveInventory.inventories = parsed;
						}
					}
				}

				var variant = this._getVariantFromOptions() || this._getSelectedVariantFromScript();
				this.selectVariant(variant ? variant.id : null, variant, { skipMasterChange: true, force: true });

				if (focusedId) {
					var focusTarget = this.querySelector('#' + CSS.escape(focusedId));
					if (focusTarget) {
						focusTarget.focus();
					}
				}
			} catch (error) {
				if (error && error.name !== 'AbortError') {
					console.warn('[product-variants-hv] Unable to refresh section', error);
				}
			}
		}
	};

	window.customElements.define('product-variants-hv', ProductVariantsHV);
})();
